import React from 'react';
import invariant from 'invariant';

import './Overlay.scss';
import Popup, { getEventRect } from 'ui/popup';

const PopupMenu = ({ items, onResolve }) => (
  <ul className="ui-PopupMenu">
    {items.map((name, index) => <li key={index} onClick={() => onResolve(index)}>{name}</li>)}
  </ul>
);

export class OverlayManager {
  nonModals = [];
  modals = [];
  root = null;
  asyncKey = 0;

  constructor(root, setState) {
    this.defaultRoot = root;
    this.setState = setState;
  }

  getRootNode() {
    return this.root || this.defaultRoot || document.body;
  }
  setRootNode(node) {
    const prevNode = this.getRootNode();
    for (const m of this.modals) {
      prevNode.removeChild(m);
    }
    for (const m of this.nonModals) {
      prevNode.removeChild(m);
    }
    this.root = node;
    const newNode = this.getRootNode();
    for (const m of this.modals) {
      newNode.appendChild(m);
    }
    for (const m of this.nonModals) {
      newNode.appendChild(m);
    }
  }

  maxZIndex() {
    if (this.modals.length) {
      return parseInt(this.modals[this.modals.length - 1].style.zIndex);
    } else {
      return this.nonModals.reduce((prev, node) => Math.max(prev, parseInt(node.style.zIndex)), 0);
    }
  }

  addOverlay(node, modal) {
    if (modal) {
      if (this.modals.length) {
        this.modals[this.modals.length - 1].classList.remove("ui-modal");
      }
      node.style.zIndex = this.maxZIndex() + 1;
      node.classList.add("ui-modal");
      this.modals.push(node);
    } else {
      if (!this.modals.length) {
        node.style.zIndex = this.maxZIndex() + 1;
      }
      this.nonModals.push(node);
    }

    this.getRootNode().appendChild(node);
  }
  removeOverlay(node) {
    let index = this.modals.indexOf(node);
    if (index >= 0) {
      this.modals.splice(index, 1);
      if (this.modals.length) {
        this.modals[this.modals.length - 1].classList.add("ui-modal");
      }
    } else if ((index = this.nonModals.indexOf(node)) >= 0) {
      this.nonModals.splice(index, 1);
    }

    this.getRootNode().removeChild(node);
  }

  moveToTop(node) {
    if (!this.modals.length) {
      invariant(this.nonModals.includes(node), `node passed to moveToTop must be an active overlay`);
      const max = this.maxZIndex();
      if (max !== parseInt(node.style.zIndex)) {
        node.style.zIndex = max + 1;
      }
    }
  }

  async(element) {
    invariant(this.setState != null, `async overlays can only be created in OverlayRoot context`);
    return new Promise((resolve, reject) => {
      this.asyncKey += 1;
      const key = this.asyncKey.toString();
      const setState = this.setState;
      const remove = () => this.setState(state => {
        const index = state.findIndex(e => e.key === key);
        if (index < 0) return state;
        //invariant(index >= 0, `async overlay not found in list`);
        state = [...state];
        state.splice(index, 1);
        return state;
      });
      element = React.cloneElement(element, {
        key,
        onResolve: result => {
          resolve(result);
          remove();
        },
        onReject: result => {
          reject(result);
          remove();
        },
        onClose: () => {
          resolve(null);
          remove();
        },
      });
      setState(state => [...state, element]);
    });
  }

  popup(pos, element) {
    const rect = (pos.getBoundingClientRect ? pos.getBoundingClientRect() : getEventRect(pos));
    return this.async(<Popup alignPosition={rect}>{element}</Popup>);
  }

  menu(pos, items) {
    return this.popup(pos, <PopupMenu items={items}/>);
  }
}

export const OverlayContext = React.createContext(new OverlayManager());

export default function OverlayRoot({ children, node }) {
  const [async, setAsync] = React.useState([]);
  const [manager] = React.useState(() => new OverlayManager(node, setAsync));
  const nodeRef = React.useRef();

  React.useEffect(() => {
    manager.setRootNode(nodeRef.current);
    return () => manager.setRootNode(null);
  }, [manager]);

  return (
    <OverlayContext.Provider value={manager}>
      <React.Fragment>
        {children}
        {async}
        {!node && <div className="ui-OverlayRoot" ref={nodeRef}/>}
      </React.Fragment>
    </OverlayContext.Provider>
  );
}
